import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

export default function BudgetSettings({ user, onRefreshUser, onRefresh, defaultCollapsed }) {
  const [saving, setSaving] = useState(false);
  const [budgetInput, setBudgetInput] = useState(user.annualBudget || 0);
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetSaved, setBudgetSaved] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rec, setRec] = useState(null);
  const [withdrawalRateInput, setWithdrawalRateInput] = useState(4);
  const [savingNormalize, setSavingNormalize] = useState(false);
  const mode = user.budgetMode || 'dynamic';
  const normalizeEnabled = user.normalizeMissingMonths ?? true;

  const [thresholdInput, setThresholdInput] = useState(user.bigPurchaseThreshold ?? 1000);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryToAdd, setCategoryToAdd] = useState('');
  const [savingExcluded, setSavingExcluded] = useState(false);
  const [savingBigPurchaseToggle, setSavingBigPurchaseToggle] = useState(false);
  const excludeBigPurchasesEnabled = user.excludeBigPurchasesFromBudget ?? false;
  const excludedCategories = user.bigPurchaseExcludedCategories || [];

  useEffect(() => {
    api.getCategoriesList().then((list) => setAvailableCategories(list)).catch(() => setAvailableCategories([]));
  }, []);

  const saveExcludedCategories = async (categories) => {
    setSavingExcluded(true);
    try {
      await api.updateBigPurchaseExcludedCategories(categories);
      onRefreshUser();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingExcluded(false);
    }
  };

  const addExcludedCategory = (category) => {
    if (!category || excludedCategories.includes(category)) return;
    saveExcludedCategories([...excludedCategories, category]);
    setCategoryToAdd('');
  };

  const removeExcludedCategory = (category) => {
    saveExcludedCategories(excludedCategories.filter((c) => c !== category));
  };

  const saveThreshold = async () => {
    const amount = parseFloat(thresholdInput);
    if (isNaN(amount) || amount < 0) return;
    setSavingThreshold(true);
    try {
      await api.updateBigPurchaseThreshold(amount);
      onRefreshUser();
      onRefresh?.();
      loadPreview();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingThreshold(false);
    }
  };

  const toggleExcludeBigPurchases = async () => {
    setSavingBigPurchaseToggle(true);
    try {
      await api.updateExcludeBigPurchasesFromBudget(!excludeBigPurchasesEnabled);
      onRefreshUser();
      onRefresh?.();
      loadPreview();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingBigPurchaseToggle(false);
    }
  };

  const toggleNormalize = async () => {
    setSavingNormalize(true);
    try {
      await api.updateNormalizeMissingMonths(!normalizeEnabled);
      onRefreshUser();
      onRefresh?.();
      loadPreview();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingNormalize(false);
    }
  };

  const loadPreview = useCallback(async () => {
    try {
      const d = await api.getSummary();
      setPreview({ ...d.budget.breakdown, bigPurchases: d.budget.bigPurchases });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadRec = useCallback(async () => {
    try {
      const d = await api.getBudgetRecommendation();
      setRec(d);
      setWithdrawalRateInput(d.withdrawalRate);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadPreview(); loadRec(); }, [loadPreview, loadRec]);
  useEffect(() => { setBudgetInput(user.annualBudget || 0); }, [user.annualBudget]);

  const setMode = async (newMode) => {
    if (newMode === mode || saving) return;
    setSaving(true);
    try {
      await api.updateBudgetMode(newMode);
      onRefreshUser();
      onRefresh?.();
      loadPreview();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const saveBudget = async (amount, source = 'manual') => {
    if (isNaN(amount) || amount < 1000) return;
    setSavingBudget(true);
    setBudgetSaved(false);
    try {
      await api.updateAnnualBudget(amount, source);
      onRefreshUser();
      onRefresh?.();
      loadPreview();
      setBudgetSaved(true);
      setTimeout(() => setBudgetSaved(false), 2500);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingBudget(false);
    }
  };

  const toggleIncome = async () => {
    const result = await api.updateBudgetRecommendationSettings(!rec.incomeIncluded, undefined);
    setRec(result);
    if (result.recommendedBudget > 0) { onRefreshUser(); onRefresh?.(); loadPreview(); }
  };

  const toggleInvestment = async () => {
    const result = await api.updateBudgetRecommendationSettings(undefined, !rec.investmentIncluded);
    setRec(result);
    if (result.recommendedBudget > 0) { onRefreshUser(); onRefresh?.(); loadPreview(); }
  };

  const saveWithdrawalRate = async (rate) => {
    await api.updateWithdrawalRate(rate);
    loadRec();
    onRefreshUser();
    onRefresh?.();
    loadPreview();
  };

  const currentBudget = parseFloat(budgetInput) || 0;
  const sliderMax = Math.max((rec?.recommendedBudget || 0) * 2, currentBudget * 1.2, 50000);
  const deficit = rec ? currentBudget - rec.recommendedBudget : null;
  const isOverRecommended = rec && rec.recommendedBudget > 0 && deficit > rec.recommendedBudget * 0.02;

  return (
    <CollapsiblePanel title="📐 Budget Settings" defaultCollapsed={defaultCollapsed}>
      <div style={styles.section}>
        <label style={styles.sectionLabel}>Base Annual Budget</label>
        <p style={styles.sectionHelp}>
          The one number everything else is calculated from.
        </p>

        {rec && (
          <div style={styles.recommendBox}>
            <div style={styles.recommendRow}>
              <span style={styles.recommendLabel}>💡 We recommend</span>
              <span style={styles.recommendValue}>{fmt.currency(rec.recommendedBudget)}/yr</span>
            </div>

            <div style={styles.sourceRow}>
              <label style={styles.sourceCheckLabel}>
                <input type="checkbox" checked={rec.incomeIncluded} onChange={toggleIncome} />
                <span>Income (take-home pay)</span>
              </label>
              <span style={styles.sourceAmt}>{fmt.currency(rec.incomeContribution)}/yr</span>
            </div>
            {rec.incomeIncluded && !user.taxState && (
              <p style={styles.sourceNote}>Set your state under Household Income for an accurate take-home estimate.</p>
            )}

            <div style={styles.sourceRow}>
              <label style={styles.sourceCheckLabel}>
                <input type="checkbox" checked={rec.investmentIncluded} onChange={toggleInvestment} />
                <span>Investment Draw ({rec.withdrawalRate}% of {fmt.currency(rec.liquidNetWorth)} liquid)</span>
              </label>
              <span style={styles.sourceAmt}>{fmt.currency(rec.investmentContribution)}/yr</span>
            </div>

            {rec.investmentIncluded && (
              <div style={styles.withdrawalSliderRow}>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.25"
                  style={styles.withdrawalSlider}
                  value={withdrawalRateInput}
                  onChange={(e) => setWithdrawalRateInput(parseFloat(e.target.value))}
                  onMouseUp={() => saveWithdrawalRate(withdrawalRateInput)}
                  onTouchEnd={() => saveWithdrawalRate(withdrawalRateInput)}
                  onKeyUp={() => saveWithdrawalRate(withdrawalRateInput)}
                />
                <span style={styles.withdrawalValue}>{withdrawalRateInput}%</span>
              </div>
            )}

            <p style={styles.recommendNote}>
              For people still working, income alone is often the whole
              picture. Retired or living off savings? Turn on the investment
              draw instead (or blend both) - a 3-4% annual draw is a commonly
              used sustainable range, but it's your call.
            </p>
            <button
              style={styles.useRecommendedBtn}
              onClick={() => { setBudgetInput(rec.recommendedBudget); saveBudget(rec.recommendedBudget, 'suggested'); }}
              disabled={savingBudget || rec.recommendedBudget <= 0}
            >
              Use this number
            </button>
            {user.budgetSource === 'suggested' && (
              <p style={styles.autoSyncNote}>
                ✓ Auto-syncing - this budget updates automatically as your
                income or investment balances change. Editing the slider
                below switches to manual.
              </p>
            )}
          </div>
        )}

        <div style={styles.sliderSection}>
          <div style={styles.sliderTopRow}>
            <span style={styles.sliderCurrentLabel}>Your Budget</span>
            <span style={styles.sliderCurrentValue}>{fmt.currency(currentBudget)}/yr</span>
          </div>
          <div style={styles.sliderWrap}>
            <input
              type="range"
              min="0"
              max={sliderMax}
              step="500"
              style={styles.slider}
              value={currentBudget}
              onChange={(e) => setBudgetInput(e.target.value)}
              onMouseUp={() => saveBudget(currentBudget, 'manual')}
              onTouchEnd={() => saveBudget(currentBudget, 'manual')}
              onKeyUp={() => saveBudget(currentBudget, 'manual')}
            />
            {rec?.recommendedBudget > 0 && (
              <div
                style={{ ...styles.recommendMarker, left: `${Math.min((rec.recommendedBudget / sliderMax) * 100, 100)}%` }}
                title={`Recommended: ${fmt.currency(rec.recommendedBudget)}`}
              />
            )}
          </div>
          <div style={styles.inputRow}>
            <span style={styles.dollarSign}>$</span>
            <input
              type="number"
              min="1000"
              step="100"
              style={styles.input}
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
            />
            <span style={styles.perYr}>/yr</span>
            <button style={styles.saveBtn} onClick={() => saveBudget(currentBudget, 'manual')} disabled={savingBudget}>
              {savingBudget ? '...' : budgetSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
        </div>

        {isOverRecommended && (
          <div style={styles.deficitWarning}>
            <span style={styles.deficitIcon}>⚠️</span>
            <div>
              <p style={styles.deficitTitle}>
                You're budgeting {fmt.currency(deficit)}/yr more than recommended.
              </p>
              <p style={styles.deficitBody}>
                At this budget, if you actually spend up to it every year,
                you'd be running roughly {fmt.currency(deficit)} in the hole
                annually ({fmt.currency(deficit / 12)}/mo) beyond what your
                selected income/investment sources cover.
              </p>
            </div>
          </div>
        )}

        {preview && (
          <p style={styles.rawNote}>
            {fmt.currency(user.annualBudget)}/yr works out to {fmt.currency(user.annualBudget / 12)}/mo
            before reserves - see the Budget Calculator below for what's actually left to spend.
          </p>
        )}
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <label style={styles.sectionLabel}>How Your Monthly Budget Adjusts</label>
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(mode === 'dynamic' ? styles.tabActive : {}) }}
            onClick={() => setMode('dynamic')}
            disabled={saving}
          >
            Adaptive
          </button>
          <button
            style={{ ...styles.tab, ...(mode === 'static' ? styles.tabActive : {}) }}
            onClick={() => setMode('static')}
            disabled={saving}
          >
            Static
          </button>
        </div>

        {mode === 'dynamic' ? (
          <div style={styles.explainBox}>
            <p style={styles.helpText}>
              <strong style={{ color: '#00d4aa' }}>Adaptive</strong> (previously
              called "Dynamic"): your annual budget is still the fixed number
              above - this doesn't look at your income. What it does is
              redistribute what's <em>left</em> of that fixed pool across the
              months remaining, based on what you've actually spent so far.
              Overspend early and later months tighten automatically;
              underspend and they loosen up.
            </p>
            {preview && (
              <div style={styles.livePreview}>
                <div style={styles.previewRow}>
                  <span style={styles.previewLabel}>Adjusted Annual Budget (after reserves)</span>
                  <span style={styles.previewValue}>{fmt.currency(preview.adjustedAnnualBudget)}</span>
                </div>
                {preview.dynamic && (
                  <div style={styles.previewRow}>
                    <span style={styles.previewLabel}>Spent so far this year</span>
                    <span style={styles.previewValue}>{fmt.currency(preview.dynamic.spendBeforeThisMonth)}</span>
                  </div>
                )}
                <div style={{ ...styles.previewRow, ...styles.previewRowFinal }}>
                  <span style={styles.previewLabel}>→ This month's budget, right now</span>
                  <span style={{ ...styles.previewValue, color: '#00d4aa' }}>{fmt.currency(preview.finalMonthlyBudget)}</span>
                </div>
                <p style={styles.calcLink}>Full step-by-step math is in the Budget Calculator below.</p>
              </div>
            )}

            <div style={styles.normalizeBox}>
              <div style={styles.normalizeRow}>
                <div>
                  <span style={styles.normalizeLabel}>Estimate months with no transaction history</span>
                  <p style={styles.normalizeHelp}>
                    If your bank connection doesn't sync a full year back (common
                    right after connecting, especially mid-year), missing months
                    are filled in using your real average from months that do
                    have data - instead of wrongly looking like $0 spent.
                  </p>
                </div>
                <button
                  style={{ ...styles.toggleSwitch, ...(normalizeEnabled ? styles.toggleSwitchOn : {}) }}
                  onClick={toggleNormalize}
                  disabled={savingNormalize}
                >
                  <span style={{ ...styles.toggleKnob, ...(normalizeEnabled ? styles.toggleKnobOn : {}) }} />
                </button>
              </div>
              {preview?.dynamic?.monthsBeforeTracking > 0 && (
                <p style={styles.normalizeStatus}>
                  {normalizeEnabled
                    ? `Currently estimating ${preview.dynamic.monthsBeforeTracking} month${preview.dynamic.monthsBeforeTracking !== 1 ? 's' : ''} at ~${fmt.currency(preview.dynamic.avgTrackedMonthSpend || 0)}/mo, based on your tracked history.`
                    : `${preview.dynamic.monthsBeforeTracking} month${preview.dynamic.monthsBeforeTracking !== 1 ? 's' : ''} this year have no transaction history and are being treated as $0 spent.`}
                </p>
              )}
            </div>

            <div style={styles.normalizeBox}>
              <div style={styles.normalizeRow}>
                <div>
                  <span style={styles.normalizeLabel}>Big Purchases</span>
                  <p style={styles.normalizeHelp}>
                    Flag one-time costs (vacations, big holiday spending, etc.)
                    at or above a threshold, and optionally pull them out of
                    your budget pace so a single big purchase doesn't make a
                    normal week or month look like a blowout. See them all
                    under Transactions → Big Purchases.
                  </p>
                </div>
              </div>

              <div style={styles.bigPurchaseFieldRow}>
                <label style={styles.bigPurchaseFieldLabel}>Threshold</label>
                <div style={styles.thresholdInputWrap}>
                  <span style={styles.dollarSign}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    style={styles.thresholdInput}
                    value={thresholdInput}
                    onChange={(e) => setThresholdInput(e.target.value)}
                  />
                  <button style={styles.smallSaveBtn} onClick={saveThreshold} disabled={savingThreshold}>
                    {savingThreshold ? '...' : 'Save'}
                  </button>
                </div>
              </div>

              <div style={styles.bigPurchaseFieldRow}>
                <label style={styles.bigPurchaseFieldLabel}>
                  Excluded categories
                  <span style={styles.bigPurchaseFieldSubLabel}> (won't count as a big purchase, even above threshold)</span>
                </label>

                {excludedCategories.length > 0 && (
                  <div style={styles.excludedChipsRow}>
                    {excludedCategories.map((cat) => (
                      <span key={cat} style={styles.excludedChip}>
                        {cat}
                        <button
                          style={styles.excludedChipRemove}
                          onClick={() => removeExcludedCategory(cat)}
                          disabled={savingExcluded}
                          title={`Remove ${cat}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div style={styles.thresholdInputWrap}>
                  <select
                    style={styles.categoryDropdown}
                    value={categoryToAdd}
                    onChange={(e) => addExcludedCategory(e.target.value)}
                    disabled={savingExcluded}
                  >
                    <option value="">+ Add a category to exclude...</option>
                    {availableCategories
                      .filter((c) => !excludedCategories.includes(c))
                      .map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                  </select>
                </div>
              </div>

              <div style={styles.normalizeRow}>
                <div>
                  <span style={styles.normalizeLabel}>Pull big purchases out of budget pace</span>
                  <p style={styles.normalizeHelp}>
                    When on, This Week/Month/Year track your normal day-to-day
                    spending only - big purchases are shown separately instead
                    of counting against your pace.
                  </p>
                </div>
                <button
                  style={{ ...styles.toggleSwitch, ...(excludeBigPurchasesEnabled ? styles.toggleSwitchOn : {}) }}
                  onClick={toggleExcludeBigPurchases}
                  disabled={savingBigPurchaseToggle}
                >
                  <span style={{ ...styles.toggleKnob, ...(excludeBigPurchasesEnabled ? styles.toggleKnobOn : {}) }} />
                </button>
              </div>
              {preview?.bigPurchases?.thisMonth > 0 && (
                <p style={styles.normalizeStatus}>
                  {fmt.currency(preview.bigPurchases.thisMonth)} in big purchases this month
                  {excludeBigPurchasesEnabled ? ' (excluded from your pace above).' : ' - not yet excluded from your pace above.'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div style={styles.explainBox}>
            <p style={styles.helpText}>
              <strong style={{ color: '#fbbf24' }}>Static:</strong> every month
              always gets the same fixed budget (annual ÷ 12), regardless of
              how prior months went.
            </p>
            {preview && (
              <div style={styles.livePreview}>
                <div style={{ ...styles.previewRow, ...styles.previewRowFinal }}>
                  <span style={styles.previewLabel}>→ Every month's budget</span>
                  <span style={{ ...styles.previewValue, color: '#fbbf24' }}>{fmt.currency(preview.staticMonthlyBudget)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </CollapsiblePanel>
  );
}

const styles = {
  section: {},
  sectionLabel: {
    display: 'block',
    color: '#e5e7eb',
    fontSize: '0.82rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '4px',
  },
  sectionHelp: {
    color: '#6b7280',
    fontSize: '0.75rem',
    lineHeight: '1.5',
    margin: '0 0 0.85rem',
  },
  recommendBox: {
    background: 'rgba(0, 212, 170, 0.06)',
    border: '1px solid rgba(0, 212, 170, 0.3)',
    borderRadius: '10px',
    padding: '0.9rem 1rem',
    marginBottom: '1.1rem',
  },
  recommendRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.6rem',
  },
  recommendLabel: {
    color: '#00d4aa',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  recommendValue: {
    color: '#fff',
    fontSize: '1.2rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  sourceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
  },
  sourceCheckLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#e5e7eb',
    fontSize: '0.76rem',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  sourceAmt: {
    color: '#9ca3af',
    fontSize: '0.76rem',
    fontFamily: "'DM Mono', monospace",
  },
  sourceNote: {
    color: '#4b5563',
    fontSize: '0.68rem',
    lineHeight: '1.5',
    margin: '2px 0 4px 22px',
  },
  withdrawalSliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    margin: '2px 0 6px 22px',
  },
  withdrawalSlider: {
    flex: 1,
    accentColor: '#00d4aa',
  },
  withdrawalValue: {
    color: '#e5e7eb',
    fontSize: '0.75rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    minWidth: '40px',
    textAlign: 'right',
  },
  recommendNote: {
    color: '#6b7280',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    margin: '0.6rem 0 0.75rem',
  },
  useRecommendedBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    fontSize: '0.78rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  autoSyncNote: {
    color: '#00d4aa',
    fontSize: '0.68rem',
    lineHeight: '1.5',
    margin: '0.6rem 0 0',
  },
  sliderSection: {
    marginBottom: '0.5rem',
  },
  sliderTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
  },
  sliderCurrentLabel: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  sliderCurrentValue: {
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  sliderWrap: {
    position: 'relative',
    marginBottom: '0.75rem',
    paddingTop: '10px',
  },
  slider: {
    width: '100%',
    accentColor: '#00d4aa',
  },
  recommendMarker: {
    position: 'absolute',
    top: 0,
    width: '2px',
    height: '8px',
    background: '#fbbf24',
    boxShadow: '0 0 4px #fbbf24',
    transform: 'translateX(-50%)',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  dollarSign: { color: '#6b7280', fontSize: '0.9rem' },
  input: {
    flex: 1,
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: "'DM Mono', monospace",
  },
  perYr: { color: '#6b7280', fontSize: '0.8rem' },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  deficitWarning: {
    display: 'flex',
    gap: '0.6rem',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    marginTop: '0.85rem',
  },
  deficitIcon: { fontSize: '1.1rem', flexShrink: 0 },
  deficitTitle: {
    color: '#ef4444',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    margin: '0 0 4px',
  },
  deficitBody: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    lineHeight: '1.5',
    margin: 0,
  },
  rawNote: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    margin: '0.75rem 0 0',
  },
  divider: { height: '1px', background: '#1e2a3a', margin: '1.1rem 0' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '0.85rem' },
  tab: {
    flex: 1,
    padding: '0.6rem',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    background: 'transparent',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  tabActive: {
    background: 'rgba(0, 212, 170, 0.1)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  explainBox: {},
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.6',
    margin: '0 0 0.85rem',
  },
  livePreview: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    padding: '3px 0',
  },
  previewRowFinal: {
    borderTop: '1px solid #1e2a3a',
    marginTop: '4px',
    paddingTop: '8px',
  },
  previewLabel: { color: '#9ca3af', fontFamily: "'Syne', sans-serif" },
  previewValue: { color: '#e5e7eb', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  calcLink: {
    color: '#4b5563',
    fontSize: '0.68rem',
    fontStyle: 'italic',
    margin: '0.6rem 0 0',
  },
  normalizeBox: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    marginTop: '0.75rem',
  },
  normalizeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  normalizeLabel: {
    color: '#e5e7eb',
    fontSize: '0.78rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  normalizeHelp: {
    color: '#6b7280',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    margin: '0.35rem 0 0',
  },
  normalizeStatus: {
    color: '#00d4aa',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    margin: '0.65rem 0 0',
    paddingTop: '0.6rem',
    borderTop: '1px solid #1e2a3a',
  },
  bigPurchaseFieldRow: {
    marginTop: '0.85rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #1e2a3a',
  },
  bigPurchaseFieldLabel: {
    color: '#e5e7eb',
    fontSize: '0.75rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
    display: 'block',
    marginBottom: '0.5rem',
  },
  bigPurchaseFieldSubLabel: {
    color: '#6b7280',
    fontWeight: '400',
    fontSize: '0.68rem',
  },
  thresholdInputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  thresholdInput: {
    flex: '0 0 120px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '6px 8px',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  excludedChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '0.6rem',
  },
  excludedChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    background: 'rgba(0, 212, 170, 0.1)',
    border: '1px solid #00d4aa',
    borderRadius: '999px',
    padding: '3px 6px 3px 12px',
    color: '#00d4aa',
    fontSize: '0.72rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  excludedChipRemove: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.9rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '2px 4px',
  },
  categoryDropdown: {
    flex: 1,
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '6px 10px',
    color: '#fff',
    fontSize: '0.78rem',
    fontFamily: "'Syne', sans-serif",

  },
  smallSaveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  toggleSwitch: {
    width: '42px',
    height: '24px',
    borderRadius: '999px',
    background: '#1e2a3a',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    flexShrink: 0,
    padding: 0,
    transition: 'background 0.2s ease',
  },
  toggleSwitchOn: {
    background: '#00d4aa',
  },
  toggleKnob: {
    position: 'absolute',
    top: '3px',
    left: '3px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#fff',
    transition: 'left 0.2s ease',
  },
  toggleKnobOn: {
    left: '21px',
  },
};
